import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-hot-toast';
import {
  DELETE_WORK_ORDER_FLAG_TEMPLATE,
  GET_WORK_ORDER_FLAG_TEMPLATES
} from '../../graphql/work-order-flags';
import { FlagIcon } from '../../utils/flag-icons';
import { calculateTintedFlagTextColor } from '../../utils/custom-flag-utils';
import WorkOrderFlagTemplateEditor from './WorkOrderFlagTemplateEditor';

function WorkOrderFlagsSection() {
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  const { data, loading, refetch } = useQuery(GET_WORK_ORDER_FLAG_TEMPLATES);
  const [deleteTemplate] = useMutation(DELETE_WORK_ORDER_FLAG_TEMPLATE);

  const templates = data?.getWorkOrderFlagTemplates || [];

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete the work order flag "${template.flagValue}"? This will remove the template from settings, but existing work orders keep their flag text.`)) {
      return;
    }

    try {
      await deleteTemplate({ variables: { input: { id: template.id } } });
      toast.success('Work order flag deleted');
      refetch();
    } catch (error) {
      toast.error(error.message || 'Failed to delete work order flag');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Work Order Flags</h2>
          <p className="mt-1 text-sm text-gray-500">
            Create and manage custom flag templates for work orders
          </p>
        </div>
        <button
          onClick={() => {
            setEditingTemplate(null);
            setShowEditor(true);
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
        >
          <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          Add Work Order Flag
        </button>
      </div>

      {templates.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
          <p className="mt-2 text-sm text-gray-500">No work order flags yet</p>
          <p className="mt-1 text-sm text-gray-400">Create your first work order flag template</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(template => {
            const textColor = calculateTintedFlagTextColor(template.backgroundColor, 0.125);
            return (
              <div key={template.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="mb-3">
                      <div
                        className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium"
                        style={{
                          backgroundColor: `${template.backgroundColor}20`,
                          color: textColor,
                          border: `1px solid ${template.backgroundColor}40`
                        }}
                      >
                        <FlagIcon iconName={template.iconName} className="h-4 w-4 mr-1" />
                        <span>{template.flagValue}</span>
                      </div>
                    </div>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div><span className="font-medium">Icon:</span> <span className="capitalize">{template.iconName}</span></div>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">Color:</span>
                        <div className="w-4 h-4 rounded border border-gray-300" style={{ backgroundColor: template.backgroundColor }} />
                        <span>{template.backgroundColor}</span>
                      </div>
                      {template.isActive === false && <div className="text-yellow-600 font-medium">Inactive</div>}
                    </div>
                  </div>

                  <div className="flex items-start space-x-2 ml-4">
                    <button
                      onClick={() => {
                        setEditingTemplate(template);
                        setShowEditor(true);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      title="Edit flag"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(template)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      title="Delete flag"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showEditor && (
        <WorkOrderFlagTemplateEditor
          template={editingTemplate}
          onClose={() => {
            setShowEditor(false);
            setEditingTemplate(null);
          }}
          onSave={refetch}
        />
      )}
    </div>
  );
}

export default WorkOrderFlagsSection;
